// ==================== API CLIENT ====================
// Uses same-origin at runtime to avoid cross-origin redirect issues on custom domains
// (e.g., production bare-domain → www redirect). Falls back to REACT_APP_BACKEND_URL for SSR safety.
const BACKEND_URL = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

async function req(method, path, body = null) {
  const opts = { method, headers: { 'Content-Type': 'application/json' } };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${API}${path}`, opts);
  if (!res.ok) {
    let msg = 'error';
    try { msg = (await res.json()).detail || msg; } catch (e) {}
    throw new Error(msg);
  }
  return res.json();
}

// ==================== AUTH ====================
export async function loginPatient(pin) {
  try { return await req('POST', '/auth/patient', { pin }); }
  catch (e) { return null; }
}
export async function loginAdmin(password) {
  try { await req('POST', '/auth/admin', { password }); return true; }
  catch (e) { return false; }
}

// ==================== PATIENTS ====================
export async function getAllPatients() { return req('GET', '/patients'); }
export async function saveAllPatients(list) { return true; } // no-op, kept for compat
export async function addPatient(patient) { return req('POST', '/patients', patient); }
export async function updatePatient(id, updates) { return req('PUT', `/patients/${id}`, updates); }
export async function deletePatient(id) { return req('DELETE', `/patients/${id}`); }
export async function getPatient(id) { return req('GET', `/patients/${id}`); }

// ==================== SESSIONS ====================
export async function getSessionsForPatient(patientId) { return req('GET', `/patients/${patientId}/sessions`); }

export async function callChatAPI({ patient_id, doctor, mood, mood_label, text, history }) {
  return req('POST', '/chat', { patient_id, doctor, mood, mood_label, text, history });
}

export async function addSession(patientId, session) {
  // Backend saves sessions automatically inside /chat. Kept for compat.
  return true;
}

// ==================== MOOD ====================
export async function getMoodLog(patientId) { return req('GET', `/patients/${patientId}/mood-log`); }
export async function addMoodLog(patientId, entry) {
  return req('POST', '/mood-log', { patient_id: patientId, ...entry });
}

// ==================== MEDS ====================
export async function getMedsLog(patientId) { return req('GET', `/patients/${patientId}/meds-log`); }
export async function markMedTaken(patientId, dateKey, timeCode, medName, taken) {
  return req('POST', '/meds-log', { patient_id: patientId, date: dateKey, time_code: timeCode, med_name: medName, taken });
}
export function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ==================== JOURNAL ====================
export async function getJournalEntries(patientId) { return req('GET', `/patients/${patientId}/journal`); }
export async function addJournalEntry(patientId, entry) {
  return req('POST', '/journal', { patient_id: patientId, ...entry });
}

// ==================== BREATHING ====================
export async function getBreathingLog(patientId) { return req('GET', `/patients/${patientId}/breathing`); }
export async function addBreathingSession(patientId, entry) {
  return req('POST', '/breathing', { patient_id: patientId, ...entry });
}

// ==================== AI FEATURES ====================
export async function aiGenerateReport(patientId) {
  return req('POST', '/ai/report', { patient_id: patientId });
}
export async function aiSuggestSupport(patientId, currentMood, context) {
  return req('POST', '/ai/suggest-support', { patient_id: patientId, current_mood: currentMood, context });
}
export async function aiAnalyzePatient(patientId) {
  return req('POST', '/ai/analyze-patient', { patient_id: patientId });
}

// ==================== VOICE ====================
export async function analyzeVoice({ patientId, blob, mode = 'chat', saveAsSession = false, filename = 'audio.webm' }) {
  const form = new FormData();
  form.append('audio', blob, filename);
  form.append('patient_id', patientId);
  form.append('mode', mode);
  form.append('save_as_session', String(saveAsSession));
  const res = await fetch(`${API}/voice/transcribe`, { method: 'POST', body: form });
  if (!res.ok) {
    let msg = 'خطأ';
    try { msg = (await res.json()).detail || msg; } catch (e) {}
    throw new Error(msg);
  }
  return res.json();
}

// ==================== MEDICATIONS CRUD ====================
export async function addMedication(patientId, med) {
  return req('POST', `/patients/${patientId}/medications`, med);
}
export async function updateMedication(patientId, index, med) {
  return req('PUT', `/patients/${patientId}/medications/${index}`, med);
}
export async function deleteMedication(patientId, index) {
  return req('DELETE', `/patients/${patientId}/medications/${index}`);
}

// ==================== HELPERS ====================
export function daysAgo(dateStr) {
  return Math.floor((Date.now() - new Date(dateStr)) / 86400000);
}

export function getStreak(moodLog) {
  if (!moodLog || moodLog.length === 0) return 0;
  const dates = new Set(moodLog.map(m => m.date));
  let streak = 0;
  const today = new Date();
  for (let i = 0; i < 365; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (dates.has(key)) streak++;
    else if (i > 0) break;
  }
  return streak;
}

export function getAffirmationOfDay(list) {
  const d = new Date();
  const dayIndex = Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 86400000);
  return list[dayIndex % list.length];
}

export function greetByTime() {
  const h = new Date().getHours();
  if (h < 5) return 'ليلة هادئة';
  if (h < 12) return 'صباحك أمل';
  if (h < 15) return 'ظهيرة مباركة';
  if (h < 18) return 'مساء طيب';
  if (h < 22) return 'مساء دافئ';
  return 'ليلة سلام';
}

// Backwards-compat: seedIfEmpty no-op (backend handles seeding)
export async function seedIfEmpty() { return true; }

// Backwards-compat mock reply now not used; kept just in case
export async function mockAIReply() { return '—'; }
